import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/lib/auth";
import { useSessionQuery, useUsersQuery } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Ban, CheckCircle, Mail, ShieldCheck, UserX } from "lucide-react";

export const Route = createFileRoute("/users")({
  component: UsersPage,
  beforeLoad: async () => {
    const { data } = await authClient.getSession();
    if (!data) {
      throw redirect({ to: "/login" });
    }
  },
});

function UsersPage() {
  const queryClient = useQueryClient();
  const { data: session } = useSessionQuery();
  const { data: usersResponse, isLoading: usersLoading, error: usersError } = useUsersQuery();
  const users = usersResponse?.users ?? [];
  const totalUsers = usersResponse?.total ?? users.length;

  const refreshUsers = async () => {
    await queryClient.invalidateQueries({ queryKey: ["users"] });
  };

  const banMutation = useMutation({
    mutationFn: async (userId: string) => {
      const result = await authClient.admin.banUser({
        userId,
        banReason: "Banned by r-auth admin",
      });

      if (result.error) {
        throw new Error(result.error.message ?? "Could not ban user");
      }
    },
    onSuccess: refreshUsers,
  });

  const unbanMutation = useMutation({
    mutationFn: async (userId: string) => {
      const result = await authClient.admin.unbanUser({ userId });

      if (result.error) {
        throw new Error(result.error.message ?? "Could not unban user");
      }
    },
    onSuccess: refreshUsers,
  });

  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const result = await authClient.admin.removeUser({ userId });

      if (result.error) {
        throw new Error(result.error.message ?? "Could not remove user");
      }
    },
    onSuccess: refreshUsers,
  });

  const actionError = banMutation.error ?? unbanMutation.error ?? removeMutation.error;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-muted-foreground">Manage authenticated users.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
          <CardDescription>{totalUsers} registered Better Auth users</CardDescription>
        </CardHeader>
        <CardContent>
          {actionError && (
            <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {actionError.message}
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    Loading users...
                  </TableCell>
                </TableRow>
              )}
              {usersError && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-destructive">
                    {usersError.message}
                  </TableCell>
                </TableRow>
              )}
              {users.map((user) => {
                const isCurrentUser = user.id === session?.user.id;
                const actionPending =
                  banMutation.isPending || unbanMutation.isPending || removeMutation.isPending;

                return (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                          {user.name?.charAt(0).toUpperCase() || "U"}
                        </div>
                        <div>
                          <p>{user.name || "Unnamed user"}</p>
                          <p className="font-mono text-xs font-normal text-muted-foreground">
                            {user.id}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="size-3.5 text-muted-foreground" />
                        <span className="text-sm">{user.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.banned ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                          <Ban className="size-3" />
                          Banned
                        </span>
                      ) : user.emailVerified ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
                          <CheckCircle className="size-3" />
                          Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          Unverified
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="rounded-md bg-muted px-2 py-1 text-xs font-medium">
                        {user.role ?? "user"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "Unknown"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {user.banned ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={actionPending}
                            onClick={() => unbanMutation.mutate(user.id)}
                          >
                            <ShieldCheck className="size-3.5" />
                            <span className="hidden sm:inline">Unban</span>
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={isCurrentUser || actionPending}
                            onClick={() => banMutation.mutate(user.id)}
                          >
                            <Ban className="size-3.5" />
                            <span className="hidden sm:inline">Ban</span>
                          </Button>
                        )}
                        <Button
                          variant="destructive"
                          size="sm"
                          disabled={isCurrentUser || actionPending}
                          onClick={() => removeMutation.mutate(user.id)}
                        >
                          <UserX className="size-3.5" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!usersLoading && !usersError && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No users found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
